<template>
  <v-card class="pa-4">
    <h2>Outstanding Assignment by Days (Branch)</h2>
    <v-row dense>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedDept"
          :items="deptOptions"
          item-text="name"
          item-value="id"
          label="Department"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-select
          v-model="selectedClass"
          :items="filteredClassOptions"
          item-text="subCode"
          item-value="id"
          label="Classification"
          dense
          outlined
          required
        />
      </v-col>
      <v-col cols="12" md="4">
        <v-menu v-model="menuStart" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startDate"
              label="Start Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              dense
              required
            />
          </template>
          <v-date-picker v-model="startDate" @change="menuStart = false" />
        </v-menu>
      </v-col>
      <v-col cols="12" md="4">
        <v-menu v-model="menuEnd" :close-on-content-click="false">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endDate"
              label="End Date"
              prepend-icon="mdi-calendar"
              readonly
              v-bind="attrs"
              v-on="on"
              :error="dateRangeInvalid"
              :error-messages="dateRangeInvalid ? ['End Date must be on or after Start Date.'] : []"
              dense
              required
            />
          </template>
          <v-date-picker v-model="endDate" @change="menuEnd = false" />
        </v-menu>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" class="d-flex justify-end">
        <v-btn
          color="primary"
          @click="generateReport"
          :disabled="!startDate || !endDate || dateRangeInvalid"
        >
          Generate Report
        </v-btn>
        <v-btn
          color="success"
          class="ml-2"
          @click="exportExcel"
          :loading="exporting"
          :disabled="!reportData.length"
        >
          <v-icon left>mdi-file-excel</v-icon>
          Export to Excel
        </v-btn>
      </v-col>
    </v-row>
    <v-row v-if="validationError">
      <v-col cols="12">
        <v-alert type="error" outlined>
          {{ validationError }}
        </v-alert>
      </v-col>
    </v-row>
    <v-snackbar
      v-model="showDateRangeSnackbar"
      timeout="3000"
      top
      right
      color="error"
      dark
      multi-line
      elevation="6"
      rounded
      max-width="420"
      class="px-3 py-2"
    >
      <div class="d-flex align-center" style="gap:12px;">
        <v-icon small>mdi-alert-circle</v-icon>
        <span class="body-2">End Date must be on or after Start Date.</span>
      </div>
      <template v-slot:action>
        <v-btn text small @click="showDateRangeSnackbar = false">CLOSE</v-btn>
      </template>
    </v-snackbar>
    <v-divider class="my-4" />
    <div v-if="reportData.length">
      <v-simple-table>
        <thead>
          <tr>
            <th>Branch</th>
            <th>Outstanding Below 30 Days</th>
            <th>Outstanding Above 30 Days</th>
            <th>Outstanding Above 45 Days</th>
            <th>Outstanding Above 60 Days</th>
            <th>Outstanding Above 90 Days</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          <template v-for="row in reportData">
            <tr :key="row.branchCode">
              <td>
                <strong>{{ row.branchCode }}</strong>
              </td>
              <td>{{ row.below30 }}</td>
              <td>{{ row.above30 }}</td>
              <td>{{ row.above45 }}</td>
              <td>{{ row.above60 }}</td>
              <td>{{ row.above90 }}</td>
              <td>{{ row.total }}</td>
            </tr>
            <tr
              v-for="detail in row.details"
              :key="row.branchCode + '-' + detail.dept + '-' + detail.class"
              class="grey--text text--darken-2"
            >
              <td>
                <span style="padding-left: 24px">
                  {{ row.branchCode }} ({{ detail.dept }} - {{ detail.class }})
                </span>
              </td>
              <td>{{ detail.below30 }}</td>
              <td>{{ detail.above30 }}</td>
              <td>{{ detail.above45 }}</td>
              <td>{{ detail.above60 }}</td>
              <td>{{ detail.above90 }}</td>
              <td>{{ detail.total }}</td>
            </tr>
          </template>
          <tr>
            <td><strong>TOTAL</strong></td>
            <td>
              <strong>{{ totals.below30 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above30 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above45 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above60 }}</strong>
            </td>
            <td>
              <strong>{{ totals.above90 }}</strong>
            </td>
            <td>
              <strong>{{ totals.total }}</strong>
            </td>
          </tr>
        </tbody>
      </v-simple-table>
    </div>
    <v-alert v-else-if="noDataMessage" type="warning" outlined class="mt-4">
      {{ noDataMessage }}
    </v-alert>
    <v-alert v-else type="info" outlined class="mt-4">
      Please select department, classification, and month, then click "Generate
      Report" to view data.
    </v-alert>
  </v-card>
</template>

<script>
import axios from "axios";
import moment from "moment";
import DeptDataService from "../../services/DeptDataService";
import SubDeptDataService from "../../services/SubDeptDataService";
import { apiConfig } from "../../config/api.config";

export default {
  name: "OutstandingDaysBranch",
    data() {
      return {
      selectedDept: "all",
      deptOptions: [],
      selectedClass: "all",
      classOptions: [],
      filteredClassOptions: [],
      startDate: null,
      endDate: null,
      menuStart: false,
      menuEnd: false,
      reportData: [],
      totals: {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      },
      exporting: false,
      noDataMessage: "",
      validationError: "",
      showDateRangeSnackbar: false,
      };
    },
    computed: {
      dateRangeInvalid() {
        if (!this.startDate || !this.endDate) return false;
        return moment(this.startDate).isAfter(this.endDate);
      },
    },
  watch: {
    selectedDept(newDept) {
      this.filterClassifications(newDept);
      this.selectedClass = "all";
    },
    classOptions: {
      handler() {
        this.filterClassifications(this.selectedDept);
      },
      immediate: true,
    },
    dateRangeInvalid(newVal) {
      // show a brief snackbar when user selects an invalid range
      this.showDateRangeSnackbar = !!newVal;
    },
  },
    methods: {
    async loadDepartments() {
      const res = await DeptDataService.getAll();
      this.deptOptions = [{ id: "all", name: "ALL DEPARTMENTS" }, ...res.data];
    },
    async loadClassifications() {
      const res = await SubDeptDataService.getAll();
      this.classOptions = [
        { id: "all", subCode: "ALL CLASSIFICATIONS", deptId: "all" },
        ...res.data,
      ];
    },
    filterClassifications(deptId) {
      // Only show ALL CLASSIFICATIONS if ALL DEPARTMENTS is selected
      if (deptId === "all") {
        this.filteredClassOptions = this.classOptions.filter(
          (c) => c.id === "all"
        );
      } else {
        const allClass = this.classOptions.find((c) => c.id === "all");
        const filtered = this.classOptions.filter((c) => c.deptId === deptId);
        this.filteredClassOptions = [allClass, ...filtered].filter(Boolean);
      }
    },
    async generateReport() {
      // clear prior state
      this.validationError = "";
      if (this.dateRangeInvalid) {
        this.validationError = "Start Date must be on or before End Date.";
        return;
      }

      this.reportData = [];
      this.totals = {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
      this.noDataMessage = "";
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          startDate: moment(this.startDate).format("YYYY-MM-DD"),
          endDate: moment(this.endDate).format("YYYY-MM-DD"),
        };
        const res = await axios.get(
          `${apiConfig.baseURL}/compliance/outstanding-days-branch`,
          { params }
        );
        this.processReportData(res.data);
        if (
          !Array.isArray(res.data.reportData) ||
          res.data.reportData.length === 0
        ) {
          const dateRangeStr = `${moment(this.startDate).format("DD MMM YYYY")} - ${moment(this.endDate).format("DD MMM YYYY")}`;
          this.noDataMessage = `No outstanding data found for ${dateRangeStr}.`;
        }
      } catch (err) {
        this.noDataMessage =
          err.message || "Failed to fetch report data. Please try again.";
      }
    },
    processReportData(data) {
      this.reportData = Array.isArray(data.reportData) ? data.reportData : [];
      const totals = {
        below30: 0,
        above30: 0,
        above45: 0,
        above60: 0,
        above90: 0,
        total: 0,
      };
      this.reportData.forEach((row) => {
        totals.below30 += row.below30;
        totals.above30 += row.above30;
        totals.above45 += row.above45;
        totals.above60 += row.above60;
        totals.above90 += row.above90;
        totals.total += row.total;
      });
      this.totals = totals;
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const params = {
          deptId: this.selectedDept,
          classificationId: this.selectedClass,
          startDate: moment(this.startDate).format("YYYY-MM-DD"),
          endDate: moment(this.endDate).format("YYYY-MM-DD"),
        };
        const url =
          `${apiConfig.baseURL}/compliance/outstanding-days-branch/export?` +
          new URLSearchParams(params).toString();
        window.open(url, "_blank");
      } catch (err) {
        this.$emit("error", err.message || "Failed to export Excel");
      } finally {
        this.exporting = false;
      }
    },
  },
    mounted() {
      this.loadDepartments();
      this.loadClassifications();
    },
  };
</script>
